import { AccessDeniedState } from './AccessDeniedState'
import { ErrorState } from './ErrorState'
import { NotFoundState } from './NotFoundState'

interface FallbackProps {
  error: unknown
  resetErrorBoundary: (...args: unknown[]) => void
}

const isSentinel = (error: unknown) =>
  error instanceof Error && (error.message === '404' || error.message === '403')

/**
 * `onError` for the route-level ErrorBoundary: logs unexpected failures once,
 * when they are caught, so the console shows what actually broke. The 404/403
 * sentinels are deliberate signals from pages, not failures, so they stay quiet.
 */
export const logRouteError = (error: unknown) => {
  if (!isSentinel(error)) console.error('[route error boundary]', error)
}

/**
 * Route-level fallback for the dashboard shell. Pages signal a missing record
 * with `throw new Error('404')` and a denied action with `'403'`; anything
 * else is an unexpected failure — a render crash, or a suspense query that
 * rejected (e.g. the package catalogue behind the create-package page).
 *
 * QTY-2057: those used to render as "page not found" too, which sent staff
 * hunting for a broken link when the real cause was a failed request. They
 * now get the generic error screen with a retry.
 */
export const CustomErrorBoundary: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const message = error instanceof Error ? error.message : String(error)

  if (message === '404') {
    return <NotFoundState />
  }

  if (message === '403') {
    return <AccessDeniedState />
  }

  return <ErrorState minHeight="700px" onRetry={() => resetErrorBoundary()} />
}
